import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone-100 flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
          <span className="font-heading text-2xl text-forest-700">Toothpix</span>
        </Link>

        {done ? (
          <div data-testid="reset-success">
            <CheckCircle2 size={28} strokeWidth={1.4} className="text-forest-600" />
            <h1 className="font-heading text-4xl text-forest-700 mt-6">Password reset</h1>
            <p className="text-sm text-forest-400 mt-4 leading-relaxed">
              Your password has been updated. Taking you to sign in…
            </p>
            <Link to="/login" className="btn-primary w-full justify-center mt-8" data-testid="reset-to-login">
              Sign in now <ArrowRight size={14} />
            </Link>
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token">
            <h1 className="font-heading text-4xl text-forest-700">Invalid link</h1>
            <p className="text-sm text-forest-400 mt-4 leading-relaxed">
              This reset link is missing or malformed. Please request a new one.
            </p>
            <Link to="/forgot-password" className="btn-primary w-full justify-center mt-8" data-testid="reset-request-new">
              Request a new link <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <div className="overline mb-3">Password reset</div>
            <h1 className="font-heading text-4xl text-forest-700">Set a new password</h1>
            <p className="text-sm text-forest-400 mt-2">Choose a new password for your clinic account.</p>

            <div className="mt-10 space-y-5">
              <div>
                <label className="label-flat">New password</label>
                <input
                  type="password"
                  className="input-flat"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="label-flat">Confirm password</label>
                <input
                  type="password"
                  className="input-flat"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="reset-confirm-input"
                />
              </div>

              {err && (
                <div className="text-sm text-clay-600 border border-clay-600/30 bg-clay-400/10 px-3 py-2" data-testid="reset-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center"
                data-testid="reset-submit"
              >
                {loading ? "Resetting…" : (<>Reset password <ArrowRight size={14} /></>)}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
